import type { ComputedRef, Ref } from 'vue';
import { compact } from 'lodash-es';
import {
  computed,
  ref,
} from 'vue';

import type {
  CommissionStep,
  CommissionStepEventPayload,
} from '@modules/affiliate-program-commissions/submodules/edit/components/CommissionSteps/types';
import type {
  IAffiliateProgramCommissionFilterFormWithErrors,
  IAffiliateProgramCommissionFilterStepPayload,
  ICommissionCountryFilterItem,
  ICommissionsConstantsResponse,
  IConditionVersions,
  ITransactionType,
} from '@modules/affiliate-program-commissions/submodules/edit/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { useConfig } from '@modules/config/composables';

export interface CommissionStepsComposable {
  firstGroupSteps: Ref<CommissionStep[]>;
  firstGroupStepResultOptions: Ref<IFormSelectInputOption<string>[]>;
  firstGroupValueSuffix: Ref<string>;
  allSecondGroupSteps: Ref<CommissionStep[]>;
  secondGroupSteps: Ref<CommissionStep[]>;
  secondGroupStepResultOptions: Ref<IFormSelectInputOption<string>[]>;
  secondGroupPaymentColumnOptions: Ref<IFormSelectInputOption<string>[]>;
  secondGroupValueSuffix: Ref<string>;
  stepComparisonOptions: Ref<IFormSelectInputOption<string>[]>;
  onStepAdd(payload: CommissionStepEventPayload): void;
  onStepRemove(index: number): void;
  onProductStepsRemove(product?: string): void;
  onStepChange(index: number, value: IAffiliateProgramCommissionFilterStepPayload): void;
}

export default function useCommissionSteps(
  activeCountryFilter: Ref<IAffiliateProgramCommissionFilterFormWithErrors | undefined>,
  transactionType: Ref<string | undefined>,
  countryFilters: Ref<ICommissionCountryFilterItem[]>,
  activeFilterIndex: Ref<number>,
  constants: Ref<ICommissionsConstantsResponse | undefined>,
  firstGroupStepTransactionType: Ref<ITransactionType>,
  secondGroupStepTransactionType: Ref<ITransactionType>,
  selectedProduct: Ref<string | undefined>,
  currentFirstGroupVersion: ComputedRef<IConditionVersions | undefined>,
  currentSecondGroupVersion: ComputedRef<IConditionVersions | undefined>,
): CommissionStepsComposable {
  const { config } = useConfig();

  const filterSteps = computed<IAffiliateProgramCommissionFilterStepPayload[]>(() => (
    activeCountryFilter.value?.condition_steps_attributes ?? []
  ));

  const firstGroupValueSuffix = computed<string>(() => ` ${config.value?.currency}`);
  const secondGroupValueSuffix = ref('%');

  const firstGroupStepResultOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!constants.value || !currentFirstGroupVersion.value) {
      return [];
    }

    const { obtained_result } = constants.value.constants.condition_steps;
    const version = currentFirstGroupVersion.value;

    let options: Record<string, string> | undefined = {};

    switch (transactionType.value) {
      case 'cpa':
        options = obtained_result.cpa[version];
        break;
      case 'cpl':
        options = obtained_result.cpl[version];
        break;
      case 'hybrid':
        options = obtained_result.hybrid.cpa[version];
        break;
      default:
        break;
    }

    return Object.entries(options ?? {}).map(([label, value]) => ({
      label,
      value,
    }));
  });

  const secondGroupStepResultOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!constants.value) {
      return [];
    }

    const { obtained_result } = constants.value.constants.condition_steps;
    const version = currentSecondGroupVersion.value;
    const product = selectedProduct.value;

    let options: Record<string, string> = {};

    if (version === 'v2') {
      switch (transactionType.value) {
        case 'revenue_share':
          options = obtained_result.revenue_share[version];
          break;
        case 'hybrid':
          options = obtained_result.hybrid.revenue_share[version];
          break;
        default:
          break;
      }
    }

    if (version === 'v1' && product) {
      switch (transactionType.value) {
        case 'revenue_share':
          options = obtained_result.revenue_share[version][product];
          break;
        case 'hybrid':
          options = obtained_result.hybrid.revenue_share[version][product];
          break;
        default:
          break;
      }
    }

    return Object.entries(options ?? {}).map(([label, value]) => ({
      label,
      value,
    }));
  });

  const secondGroupPaymentColumnOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!constants.value) {
      return [];
    }

    const { payment_column } = constants.value.constants.condition_steps;
    const version = currentSecondGroupVersion.value;
    const product = selectedProduct.value;

    let options: Record<string, string> = {};

    if (version === 'v2') {
      switch (transactionType.value) {
        case 'revenue_share':
          options = payment_column.revenue_share[version];
          break;
        case 'hybrid':
          options = payment_column.hybrid.revenue_share[version];
          break;
        default:
          break;
      }
    }

    if (version === 'v1' && product) {
      switch (transactionType.value) {
        case 'revenue_share':
          options = payment_column.revenue_share[version][product];
          break;
        case 'hybrid':
          options = payment_column.hybrid.revenue_share[version][product];
          break;
        default:
          break;
      }
    }

    return Object.entries(options ?? {}).map(([label, value]) => ({
      label,
      value,
    }));
  });

  const stepComparisonOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!constants.value) {
      return [];
    }

    return Object.entries(constants.value.constants.condition_steps.condition_for_comparison).map(([value, label]) => ({
      label,
      value,
    }));
  });

  const allSteps = computed<CommissionStep[]>(() => {
    const items: CommissionStep[] = [];

    filterSteps.value.forEach((item, index) => {
      if (!item._destroy) {
        items.push({
          index,
          value: item,
        });
      }
    });

    return items;
  });

  const firstGroupSteps = computed<CommissionStep[]>(() => (
    allSteps.value.filter((step) => step.value.type_in_part_condition === firstGroupStepTransactionType.value)
  ));

  const allSecondGroupSteps = computed<CommissionStep[]>(() => (
    allSteps.value.filter((step) => step.value.type_in_part_condition === secondGroupStepTransactionType.value)
  ));

  const secondGroupSteps = computed<CommissionStep[]>(() => (
    currentSecondGroupVersion.value === 'v2' ? [...allSecondGroupSteps.value] : allSecondGroupSteps.value.filter((step) => step.value.product === selectedProduct.value)
  ));

  function getObtainedResultAndPaymentColumn(
    steps: IAffiliateProgramCommissionFilterStepPayload[],
    payload: CommissionStepEventPayload,
  ): { obtained_result?: string; payment_column?: string } {
    const stepsByType = steps.filter((step) => step.type_in_part_condition === payload.transactionType && !step._destroy);
    const stepsByProduct = payload.product ? stepsByType.filter((step) => step.product === payload.product) : stepsByType;
    const obtained_result = stepsByProduct[0]?.condition_step_items_attributes[0]?.obtained_result;
    const payment_column = stepsByProduct[0]?.payment_column;

    return { obtained_result, payment_column };
  }

  function updateObtainedResultAndPaymentColumn(
    steps: IAffiliateProgramCommissionFilterStepPayload[],
    newStep: IAffiliateProgramCommissionFilterStepPayload,
  ): IAffiliateProgramCommissionFilterStepPayload[] {
    return steps.map((step) => {
      if (step._destroy || step.type_in_part_condition !== newStep.type_in_part_condition) {
        return step;
      }

      if (currentSecondGroupVersion.value === 'v2') {
        return {
          ...step,
          payment_column: newStep.payment_column,
          condition_step_items_attributes: [{
            ...step.condition_step_items_attributes[0],
            obtained_result: newStep.condition_step_items_attributes[0]?.obtained_result,
          }],
        };
      }

      if (newStep.product) {
        if (step.product === newStep.product) {
          return {
            ...step,
            payment_column: newStep.payment_column,
            condition_step_items_attributes: [{
              ...step.condition_step_items_attributes[0],
              obtained_result: newStep.condition_step_items_attributes[0]?.obtained_result,
            }],
          };
        }
        return step;
      }

      return {
        ...step,
        condition_step_items_attributes: [{
          ...step.condition_step_items_attributes[0],
          obtained_result: newStep.condition_step_items_attributes[0]?.obtained_result,
        }],
      };
    });
  }

  function onStepAdd(payload: CommissionStepEventPayload): void {
    const activeFilter = countryFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      const { obtained_result, payment_column } = getObtainedResultAndPaymentColumn(activeFilter.condition_steps_attributes, payload);

      const newValue: IAffiliateProgramCommissionFilterStepPayload = {
        payment_size: undefined,
        payment_column,
        type_in_part_condition: payload.transactionType,
        product: payload.product,
        condition_step_items_attributes: obtained_result ? [{ obtained_result, condition_for_comparison: '' }] : [],
      };

      countryFilters.value[activeFilterIndex.value] = {
        ...activeFilter,
        condition_steps_attributes: [
          ...activeFilter.condition_steps_attributes,
          newValue,
        ],
      };
    }
  }

  function onStepRemove(index: number): void {
    const activeFilter = countryFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      const attributes = [...activeFilter.condition_steps_attributes];
      const attribute = attributes[index];

      if (attribute) {
        if (attribute.id) {
          attributes.splice(index, 1, {
            ...attribute,
            _destroy: 1,
          });
        } else {
          attributes.splice(index, 1);
        }

        countryFilters.value[activeFilterIndex.value] = {
          ...activeFilter,
          condition_steps_attributes: attributes,
        };
      }
    }
  }

  function onProductStepsRemove(product?: string): void {
    const activeFilter = countryFilters.value[activeFilterIndex.value];

    if (activeFilter) {
      const attributes = compact(activeFilter.condition_steps_attributes.map((step) => {
        if (step.type_in_part_condition === secondGroupStepTransactionType.value && step.product === product) {
          return step.id ? { ...step, _destroy: 1 } : null;
        }
        return step;
      }));

      countryFilters.value[activeFilterIndex.value] = {
        ...activeFilter,
        condition_steps_attributes: attributes,
      };
    }
  }

  function onStepChange(index: number, values: IAffiliateProgramCommissionFilterStepPayload): void {
    const activeFilter = countryFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      const attributes = [...activeFilter.condition_steps_attributes];
      const attribute = attributes[index];

      if (attribute) {
        attributes.splice(index, 1, {
          ...attribute,
          ...values,
        });

        countryFilters.value[activeFilterIndex.value] = {
          ...activeFilter,
          condition_steps_attributes: updateObtainedResultAndPaymentColumn(attributes, values),
        };
      }
    }
  }

  return {
    firstGroupSteps,
    firstGroupStepResultOptions,
    firstGroupValueSuffix,
    allSecondGroupSteps,
    secondGroupSteps,
    secondGroupStepResultOptions,
    secondGroupValueSuffix,
    secondGroupPaymentColumnOptions,
    stepComparisonOptions,
    onStepAdd,
    onStepRemove,
    onProductStepsRemove,
    onStepChange,
  };
}
